import { ReactNode, useEffect, useState } from "react";
import { useViewportVisibilityForAnimations } from "~/components/1_atoms/useViewportVisibility";

export interface ResponsiveImageSrc {
  mobile: string;
  desktop: string;
}

export interface ImageBannerProps {
  src: string | ResponsiveImageSrc;
  alt?: string;
  children?: ReactNode;
}

/**
 * Detects if the current device is mobile based on screen width
 * Uses consistent breakpoints: mobile and tablet < 1280px, desktop >= 1280px
 * Follows the same pattern as useViewportVisibility hook
 */
function getDeviceType() {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth < 1280 ? "mobile" : "desktop";
}

/**
 * ImageBanner - A full-width banner component for displaying hero-style images
 */
export function ImageBanner({ src, alt = "", children }: ImageBannerProps) {
  const { ref, isVisible } = useViewportVisibilityForAnimations<HTMLDivElement>();
  const [currentImageSrc, setCurrentImageSrc] = useState<string>("");

  // Determine the appropriate image source based on device type and src format
  useEffect(() => {
    const updateImageSrc = () => {
      if (typeof src === "string") {
        // Single image for all devices
        return src;
      } else {
        // Responsive images - choose based on device type
        const deviceType = getDeviceType();
        return deviceType === "mobile" ? src.mobile : src.desktop;
      }
    };

    // Set initial image source using setTimeout to avoid ESLint warning
    const timeoutId = setTimeout(() => {
      setCurrentImageSrc(updateImageSrc());
    }, 0);

    // Listen for window resize to update image source if needed
    const handleResize = () => {
      if (typeof src === "object") {
        setCurrentImageSrc(updateImageSrc());
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [src]);

  return (
    <div ref={ref} className="tg-image-banner">
      <div
        className={`${children ? "tg-image-banner__grid" : ""} tg-image-banner__wrapper`}
      >
        <img
          src={currentImageSrc}
          alt={alt}
          className={`tg-image-banner__image transition-all duration-700 ease-in-out ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
        />
        {children ? (
          <div
            className={`z-10 col-span-12 transition-all delay-350 duration-700 ease-in-out xl:col-start-3 xl:col-end-11 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
